import { apiFetch } from './client';
import { normalizeOrder } from './orders';

export const DELIVERY_ERRORS = {
  already_assigned: 'Another rider already claimed this order.',
  invalid_status: 'This order is not ready for that action.',
  not_assigned: 'This order is not assigned to you.',
  invalid_transition: 'This order has already moved on — refresh to see its latest status.',
  pod_required: 'Capture a proof-of-delivery photo before delivering.',
  cod_must_be_collected: 'Confirm payment collection before delivering.',
  artifacts_bucket_missing: 'Photo upload is not available right now — try again shortly.',
  not_found: 'This order could not be found — it may have been removed.',
  forbidden: 'You do not have permission to do this.',
};

export async function listDeliveryOrders(status, token) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const raw = await apiFetch(`/delivery/orders${qs}`, { token });
  const listPhone = raw?.phone || raw?.customer_phone || null;
  return (raw?.orders || []).map((item) => {
    const normalized = normalizeOrder(item);
    if (!normalized.customer.phone && listPhone) {
      normalized.customer.phone = listPhone;
    }
    return normalized;
  });
}

export async function claimOrder(orderId, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/claim`, { method: 'POST', token });
  return {
    order: normalizeOrder(raw),
    claimed: !!raw?.claimed,
    alreadyYours: !!raw?.already_yours,
  };
}

export async function startDelivery(orderId, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/start`, { method: 'POST', token });
  return normalizeOrder(raw);
}

async function presignPod(orderId, { kind = 'photo', contentType }, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/pod`, {
    method: 'POST',
    token,
    body: { action: 'presign', kind, content_type: contentType },
  });
  return { uploadUrl: raw?.upload_url || null, s3Key: raw?.s3_key || null };
}

async function putToPresignedUrl(uploadUrl, blob, contentType) {
  // Content-Type must match the presign while BE still signs it
  // (X-Amz-SignedHeaders includes content-type). OPTIONS 403 means S3 CORS
  // must allow this origin + PUT (+ content-type header).
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: blob,
    });
  } catch {
    throw new Error('Photo upload failed — check your connection and try again.');
  }
  if (!response.ok) throw new Error('Photo upload failed — try again.');
}

export async function uploadPodPhoto(orderId, blob, token, { kind = 'photo' } = {}) {
  const contentType = blob.type || 'image/jpeg';
  // Unique `kind` values keep multi-photo pickup uploads from overwriting one S3 key.
  const { uploadUrl, s3Key } = await presignPod(orderId, { kind, contentType }, token);
  if (!uploadUrl || !s3Key) throw new Error('artifacts_bucket_missing');
  await putToPresignedUrl(uploadUrl, blob, contentType);
  return s3Key;
}

export async function confirmPod(orderId, { photoKey, signatureKey } = {}, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/pod`, {
    method: 'POST',
    token,
    body: {
      action: 'confirm',
      ...(photoKey ? { pod_photo_s3_key: photoKey } : {}),
      ...(signatureKey ? { pod_signature_s3_key: signatureKey } : {}),
    },
  });
  return { ok: !!raw?.ok };
}

export async function deliverOrder(orderId, { codCollected = true } = {}, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/deliver`, {
    method: 'POST',
    token,
    body: { cod_collected: codCollected },
  });
  return normalizeOrder(raw);
}

export async function failDelivery(orderId, reason, token) {
  const raw = await apiFetch(`/delivery/orders/${encodeURIComponent(orderId)}/fail`, {
    method: 'POST',
    token,
    body: { reason },
  });
  return normalizeOrder(raw);
}
